import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart, BarChart } from 'echarts/charts';
import { TooltipComponent, LegendComponent, GridComponent, DataZoomComponent, ToolboxComponent } from 'echarts/components';

echarts.use([
  CanvasRenderer,
  LineChart,
  BarChart,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
  ToolboxComponent
]);

function getThemeBackgroundColor() {
  const mainElement = document.querySelector('main');
  if (mainElement) {
    const computedStyle = window.getComputedStyle(mainElement);
    const backgroundColor = computedStyle.backgroundColor;
    
    if (backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'transparent') {
      return backgroundColor;
    }
  }

  const vAppElement = document.querySelector('.v-application');
  if (vAppElement) {
    const computedStyle = window.getComputedStyle(vAppElement);
    const backgroundColor = computedStyle.backgroundColor;
    
    if (backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'transparent') {
      return backgroundColor;
    }
  }

  const htmlElement = document.documentElement;
  const isDarkTheme = htmlElement.classList.contains('dark');
  
  if (isDarkTheme) {
    return '#213542';
  } else {
    return '#F5F5F5';
  }
}

function getChartFontColor() {
  // Try to get text color from computed styles first
  const mainElement = document.querySelector('main');
  if (mainElement) {
    const computedStyle = window.getComputedStyle(mainElement);
    const color = computedStyle.color;
    
    if (color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent') {
      return color;
    }
  }

  // Fallback: Detect theme based on background color
  const backgroundColor = getThemeBackgroundColor();
  
  // If background is dark, use white text; if light, use black text
  if (backgroundColor === '#213542' || backgroundColor.includes('rgb(33, 53, 66)')) {
    return '#FFFFFF';
  } else {
    return '#000000';
  }
}

export function downloadChartAsPNG(chart, filename = 'chart.png', seriesData = null) {
  if (!chart) return;

  const originalOption = chart.getOption();
  const backgroundColor = getThemeBackgroundColor();
  const chartFontColor = getChartFontColor();

  // Create a temporary hidden div for off-screen rendering with legend
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.width = `${chart.getWidth()}px`;
  tempDiv.style.height = `${chart.getHeight() + 80}px`;
  document.body.appendChild(tempDiv);

  // Initialize a new ECharts instance on the temp div
  const tempChart = echarts.init(tempDiv);

  // Create download option with legend
  const downloadOption = JSON.parse(JSON.stringify(originalOption)); // Deep copy

  // Build legend data and update series names
  let legendData = [];

  // Helper function to check if series has any non-zero data
  const hasVisibleData = (series) => {
    if (!series.data || !Array.isArray(series.data)) return false;
    return series.data.some(value => {
      const numValue = typeof value === 'object' ? (value?.value || 0) : value;
      return numValue !== 0 && numValue !== null && numValue !== undefined;
    });
  };

  // If seriesData is provided, use it as the source of truth
  if (seriesData && Array.isArray(seriesData) && seriesData.length > 0) {
    // Filter seriesData to only include series with visible data
    const visibleSeriesData = seriesData.filter(s => hasVisibleData(s));

    // Build a map of colors to series data for matching
    // Combine titles from multiple series with the same color
    const colorToSeriesData = new Map();
    visibleSeriesData.forEach(s => {
      const color = s.color || s.itemStyle?.color || s.lineStyle?.color;
      if (color) {
        const colorKey = color.toLowerCase();
        if (colorToSeriesData.has(colorKey)) {
          // Merge titles from multiple series with same color
          const existing = colorToSeriesData.get(colorKey);
          const existingTitles = existing.titles || (existing.name ? [existing.name] : []);
          const newTitles = s.titles || (s.name ? [s.name] : []);
          existing.titles = [...new Set([...existingTitles, ...newTitles])]; // Deduplicate
        } else {
          colorToSeriesData.set(colorKey, { ...s });
        }
      }
    });

    // Filter and update downloadOption.series based on visible seriesData
    downloadOption.series = downloadOption.series.map(series => {
      const seriesColor = (series.color || series.itemStyle?.color || series.lineStyle?.color || '').toLowerCase();
      const matchingSeriesData = colorToSeriesData.get(seriesColor);

      if (matchingSeriesData) {
        const displayName = matchingSeriesData.titles && Array.isArray(matchingSeriesData.titles)
          ? matchingSeriesData.titles.join(', ')
          : matchingSeriesData.name;

        legendData.push({
          name: displayName,
          icon: 'circle'
        });

        return {
          ...series,
          name: displayName
        };
      }
      return null; // Mark for removal - no matching visible data
    }).filter(s => s !== null);
  } else {
    // Extract series names from the chart for legend - only those with visible data
    if (downloadOption.series && Array.isArray(downloadOption.series)) {
      downloadOption.series = downloadOption.series.filter(s => hasVisibleData(s));
      legendData = downloadOption.series
        .filter(s => s.name)
        .map(s => ({
          name: s.name,
          icon: 'circle'
        }));
    }
  }

  // Add legend to the download option
  if (legendData.length > 0) {
    downloadOption.legend = [{
      orient: 'horizontal',
      bottom: 10,
      data: legendData,
      textStyle: {
        color: chartFontColor
      }
    }];
  }

  // Adjust grid to make space for the legend
  if (downloadOption.grid) {
    if (Array.isArray(downloadOption.grid)) {
      downloadOption.grid[0].bottom = '12%';
    } else {
      downloadOption.grid.bottom = '12%';
    }
  }

  // Hide dataZoom (bottom slider bar) in download
  if (downloadOption.dataZoom) {
    if (Array.isArray(downloadOption.dataZoom)) {
      downloadOption.dataZoom = downloadOption.dataZoom.map(zoom => ({
        ...zoom,
        show: false
      }));
    } else {
      downloadOption.dataZoom.show = false;
    }
  }

  // Set background color and disable animation
  downloadOption.backgroundColor = backgroundColor;
  downloadOption.animation = false;

  // Set the option on the temp chart
  tempChart.setOption(downloadOption);

  // Force resize to ensure proper layout
  tempChart.resize();

  // Add a small delay to ensure rendering completes
  setTimeout(() => {
    // Generate the image URL
    const url = tempChart.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: backgroundColor
    });

    // Clean up: dispose the temp chart and remove div
    tempChart.dispose();
    document.body.removeChild(tempDiv);

    // Trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
  }, 100);
}

export function formatTimeValue(value) {
  if (value === null || value === undefined || value === 'N/A') {
    return 'N/A';
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return 'N/A';
  }

  if (numValue < 1) {
    const hours = numValue * 24;
    if (hours < 1) {
      const minutes = hours * 60;
      if (minutes < 1) {
        const seconds = minutes * 60;
        return `${seconds.toFixed(1)}s`;
      }
      return `${minutes.toFixed(1)}m`;
    }
    return `${hours.toFixed(1)}h`;
  }

  return `${numValue.toFixed(1)}d`;
}